import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import Datastore from "../../communication/Datastore";
import store, { IAppState } from "../Store";
import Calendar from "../../types/organiser/Calendar";
import PublishedOrganiser from "../../types/organiser/PublishedOrganiser";
import { SystemFileType } from "../../types/enums/SystemFileType";
import { SystemFileVersion } from "../../types/enums/SystemFileVersion";

// Define the type for our state store entry
type OrganisersState = {
    organisers: Calendar[];
    published: PublishedOrganiser[];
    template: Calendar;
    status: string;
    error: string;
    publishedStatus: string;
    publishedError: string;
    newStatus: string;
    newError: string;
};

// Define the initial state for our state store entry
const initialState: OrganisersState = {
    organisers: [],
    published: [],
    template: null,
    status: "idle",
    error: null,
    publishedStatus: "idle",
    publishedError: null,
    newStatus: "idle",
    newError: null,
};

// Create a slice to hold all our reducers
const organisersSlice = createSlice({
    name: "organisers",
    initialState: initialState,
    reducers: {
        removeOrganiser: (state, action) => {
            state.organisers = state.organisers.filter(
                item => !action.payload.some(id => id === item.id),
            );
        },
        removePublished: (state, action) => {
            state.published = state.published.filter(
                item => !action.payload.some(id => id === item.id),
            );
        },
        resetOrganiserStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                return {
                    organisers:
                        rawData?.payload?.organisers?.organisers?.map(
                            item => new Calendar(item),
                        ) || [],
                    published:
                        rawData?.payload?.organisers?.published?.map(
                            item => new PublishedOrganiser(item),
                        ) || [],
                    template: rawData?.payload?.organisers?.template
                        ? new Calendar(rawData.payload.organisers.template)
                        : null,
                    status:
                        rawData?.payload?.organisers?.organisers?.length > 0
                            ? "succeeded"
                            : "idle",
                    error: null,
                    publishedStatus:
                        rawData?.payload?.organisers?.published?.length > 0
                            ? "succeeded"
                            : "idle",
                    publishedError: null,
                    newStatus: rawData?.payload?.organisers?.template
                        ? "succeeded"
                        : "idle",
                    newError: null,
                };
            })
            .addCase(fetchOrganisers.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchOrganisers.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.organisers = action.payload.map(
                    item => new Calendar(item),
                );
            })
            .addCase(fetchOrganisers.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchPublishedOrganisers.pending, (state, action) => {
                state.publishedStatus = "loading";
            })
            .addCase(fetchPublishedOrganisers.fulfilled, (state, action) => {
                state.publishedStatus = "succeeded";
                state.published = action.payload.map(
                    item => new PublishedOrganiser(item),
                );
            })
            .addCase(fetchPublishedOrganisers.rejected, (state, action) => {
                state.publishedStatus = "failed";
                state.publishedError = action.error.message;
            })
            .addCase(fetchTemplate.pending, (state, action) => {
                state.newStatus = "loading";
            })
            .addCase(fetchTemplate.fulfilled, (state, action) => {
                state.newStatus = "succeeded";
                state.template = new Calendar(action.payload);
            })
            .addCase(fetchTemplate.rejected, (state, action) => {
                state.newStatus = "failed";
                state.newError = action.error.message;
            });
    },
});

const { removePublished, resetOrganiserStatus } = organisersSlice.actions;

// This is a thunk, they are used to load the state
const fetchOrganisers = createAsyncThunk(
    "organisers/fetchOrganisers",
    (): Promise<Calendar[]> => Datastore.getType<Calendar[]>("api/Organisers"),
    {
        condition: (_, { getState }) =>
            (getState() as IAppState).db.organisers.status === "idle",
    },
);
const fetchPublishedOrganisers = createAsyncThunk(
    "organisers/fetchPublishedOrganisers",
    (): Promise<PublishedOrganiser[]> =>
        Datastore.getType<PublishedOrganiser[]>("api/Organisers/Timetables"),
    {
        condition: (_, { getState }) =>
            (getState() as IAppState).db.organisers.publishedStatus === "idle",
    },
);
const fetchTemplate = createAsyncThunk(
    "organisers/fetchTemplate",
    (): Promise<Calendar> => Datastore.getType<Calendar>("api/Organisers/New"),
    {
        condition: (_, { getState }) =>
            (getState() as IAppState).db.organisers.newStatus !== "loading",
    },
);
export const deleteOrganisers = createAsyncThunk(
    "organisers/deleteOrganisers",
    async (organisers: PublishedOrganiser[], thunkAPI) => {
        await Datastore.post(
            `api/SystemIO/Files/Recycle`,
            organisers.map(org => ({
                id: org.id.toString(),
                fileType: SystemFileType.DailyOrganiserFile,
                version: SystemFileVersion.Version9,
            })),
        );

        await thunkAPI.dispatch(removePublished(organisers.map(org => org.id)));
    },
);
export const initialiseOrganiserDetails = async (): Promise<void> => {
    await store.dispatch(fetchOrganisers());
};
export const initialisePublishedOrganiserDetails = async (): Promise<void> => {
    await store.dispatch(fetchPublishedOrganisers());
};
export const initialiseOrganiserTemplate = async (): Promise<void> => {
    await store.dispatch(fetchTemplate());
};
export const clearOrganiserDetails = () => {
    store.dispatch(resetOrganiserStatus());
};
export const reinitialiseOrganiserDetails = async (): Promise<void> => {
    store.dispatch(resetOrganiserStatus());
    await store.dispatch(fetchOrganisers());
};

// We have to export the reducer as this is added to the state store
export default organisersSlice.reducer;
